import { Button, Divider, Stack, Typography } from "@mui/material";
import React, { useCallback } from "react";
import Modal, { CommonModalProps } from "..";
import { billingService, cacheService } from "../../../Core/Api/services";
import SelectableCard from "../../Cards/SelectableCard";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { UserHasLicence } from "../../../Utils/helpers";
import { Licences } from "../../../Core/Api/Types/licences";
import { produce } from "immer";
import OutlinedCheckbox from "../../Forms/OutlinedCheckbox";
import { useTranslation } from "react-i18next";

export type ModalBillingChangeLicencesElementProps = {
  priceModel: any;
  onSuccess: () => void;
  onClose: () => void;
};

export type ModalBillingChangeLicencesProps = CommonModalProps<ModalBillingChangeLicencesElementProps>;

const ModalBillingChangeLicences = React.forwardRef<any, ModalBillingChangeLicencesProps>((props, ref) => {
  const { t } = useTranslation();
  const { onClose, onSuccess, closeModal, ...modalProps } = props;
  const { priceModel } = modalProps;
  const [newLicences, setNewLicences] = React.useState<Licences[]>(cacheService.GetLicenses());
  const [sure, setSure] = React.useState<boolean>(false);
  const enqueueCustomSnackbar = useCustomSnackbar();

  const handleCancelClick = () => {
    closeModal();
    onClose && onClose();
  };

  const onClickLicence = (lic: Licences) => {
    setNewLicences(
      produce((draft) => {
        if (draft.includes(lic)) draft.splice(draft.indexOf(lic), 1);
        else draft.push(lic);
      })
    );
  };

  const memoNewChargePrice: number = React.useMemo(() => {
    let lics = priceModel.global.licences.filter((x) => !x.included && !UserHasLicence(x.type));
    let price: number = 0;

    for (let i = 0; i < lics.length; i++) {
      if (newLicences.includes(lics[i].type)) {
        price += lics[i].basePrice;
      }
    }

    return price;
  }, [priceModel, newLicences]);

  const onConfirm = useCallback(() => {
    billingService
      .UpdateMySubscription({
        newOfferId: null,
        newLicences: newLicences,
        simulate: false,
      })
      .then((response) => {
        if (response.success) {
          cacheService.UpdateLicenses(newLicences);
          enqueueCustomSnackbar(t("alert:success.subscriptionUpdated"), "success");
          closeModal();
          onSuccess && onSuccess();
        } else enqueueCustomSnackbar(t("alert:error.changeLicensingOptions", { message: response.message }), "warning");
      });
  }, [newLicences, t]);

  const _renderLicenceList = (subscribed: boolean) => {
    return (
      <>
        {priceModel.global.licences
          .filter((x) => !x.included && UserHasLicence(x.type) === subscribed)
          .map((lic) => (
            <SelectableCard
              key={lic.type}
              onClick={() => onClickLicence(lic.type)}
              selected={newLicences.includes(lic.type)}
            >
              <Stack>
                <Typography variant="body1">{lic.type}</Typography>
                <Typography variant="body2" color="text.tier">
                  {lic.basePrice} EUR {t("common:monthly")}
                </Typography>
              </Stack>
            </SelectableCard>
          ))}
      </>
    );
  };

  return (
    <Modal
      title={t("modal:title.changeSubscriptionLicense")}
      reference={ref}
      closeModal={handleCancelClick}
      width={504}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button variant="contained" color="primary" onClick={onConfirm} disabled={!sure}>
            {t("common:confirm")}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2}>
        <Typography variant="h6">{t("modal:modalBillingChangeLicences.body.newLicenceText")}</Typography>
        {_renderLicenceList(false)}

        <Divider sx={{ mt: 2, mb: 2 }} />

        <Typography variant="h6">{t("modal:modalBillingChangeLicences.body.optionalLicenceText")}</Typography>
        {_renderLicenceList(true)}

        <Divider sx={{ mt: 2, mb: 2 }} />

        <OutlinedCheckbox
          mt={2.5}
          checked={sure}
          onClick={() => {
            setSure((prev) => (prev = !prev));
          }}
        >
          <Typography variant="body2" fontWeight={500}>
            {t("modal:modalBillingChangeLicences.body.licenceCheckBox.wantToChangeMyLicensing")}
          </Typography>
          <Typography variant="body2" color="text.tier">
            {t("modal:modalBillingChangeLicences.body.licenceCheckBox.chargedMonthly", { price: memoNewChargePrice })}{" "}
            {memoNewChargePrice !== 0
              ? t("modal:modalBillingChangeLicences.body.licenceCheckBox.proratedPaymentNotice")
              : ""}
          </Typography>
        </OutlinedCheckbox>
      </Stack>
    </Modal>
  );
});

export default ModalBillingChangeLicences;
